import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  Supplier,
  SupplierStatus,
  Customer,
  CustomerStatus,
  WorkOrder,
  Agreement,
  CompanyDocument,
  ContractDashboardStats,
  ContractApiResponse,
  ContractListResponse,
  SupplierFilterParams,
  CustomerFilterParams,
  AgreementFilterParams,
  DocumentRequirement,
  ContractFilterParams,
  PaginatedResponse,
  B2BListingRequest,
  B2BContact,
  B2BListingResponse
} from '../types/Contract';

@Injectable({
  providedIn: 'root'
})
export class ContractService {

  // API Endpoints
  private readonly BASE_PATH = 'contracts';

  // Refresh signal for same-route menu clicks
  private refreshSubject = new Subject<void>();
  refresh$ = this.refreshSubject.asObservable();

  triggerRefresh(): void {
    this.refreshSubject.next();
  }

  // BehaviorSubjects for state management
  private selectedSupplierSubject = new BehaviorSubject<Supplier | null>(null);
  private selectedCustomerSubject = new BehaviorSubject<Customer | null>(null);
  private selectedWorkOrderSubject = new BehaviorSubject<WorkOrder | null>(null);

  // Observable streams
  selectedSupplier$ = this.selectedSupplierSubject.asObservable();
  selectedCustomer$ = this.selectedCustomerSubject.asObservable();
  selectedWorkOrder$ = this.selectedWorkOrderSubject.asObservable();

  constructor(private apiService: ApiService) {}

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  getDashboardStats(): Observable<ContractDashboardStats> {
    return this.apiService.query(`${this.BASE_PATH}/dashboard/stats`).pipe(
      map((res: ContractApiResponse<ContractDashboardStats>) => res.data)
    );
  }

  // ============================================================================
  // SUPPLIERS
  // ============================================================================

  getSuppliers(params?: SupplierFilterParams): Observable<ContractListResponse<Supplier>> {
    const payload = this.buildFilterPayload(params);
    return this.apiService.create(`${this.BASE_PATH}/suppliers/list`, payload).pipe(
      map((res: ContractApiResponse<ContractListResponse<Supplier>>) => res.data)
    );
  }

  getSupplierById(supplierId: string): Observable<Supplier> {
    return this.apiService.query(`${this.BASE_PATH}/suppliers/${supplierId}`).pipe(
      map((res: ContractApiResponse<Supplier>) => {
        this.selectedSupplierSubject.next(res.data);
        return res.data;
      })
    );
  }

  createSupplier(supplier: Partial<Supplier>): Observable<Supplier> {
    return this.apiService.create(`${this.BASE_PATH}/suppliers`, supplier).pipe(
      map((res: ContractApiResponse<Supplier>) => res.data)
    );
  }

  updateSupplier(supplierId: string, supplier: Partial<Supplier>): Observable<Supplier> {
    return this.apiService.updatePut(`${this.BASE_PATH}/suppliers/${supplierId}`, supplier).pipe(
      map((res: ContractApiResponse<Supplier>) => res.data)
    );
  }

  deleteSupplier(supplierId: string): Observable<any> {
    return this.apiService.delete(`${this.BASE_PATH}/suppliers/${supplierId}`);
  }

  // Supplier Documents
  getSupplierDocuments(supplierId: string): Observable<CompanyDocument[]> {
    return this.apiService.query(`${this.BASE_PATH}/suppliers/${supplierId}/documents`).pipe(
      map((res: ContractApiResponse<CompanyDocument[]>) => res.data)
    );
  }

  uploadSupplierDocument(supplierId: string, document: Partial<CompanyDocument>): Observable<CompanyDocument> {
    return this.apiService.create(`${this.BASE_PATH}/suppliers/${supplierId}/documents`, document).pipe(
      map((res: ContractApiResponse<CompanyDocument>) => res.data)
    );
  }

  updateSupplierDocument(supplierId: string, documentId: string, document: Partial<CompanyDocument>): Observable<CompanyDocument> {
    return this.apiService.updatePut(`${this.BASE_PATH}/suppliers/${supplierId}/documents/${documentId}`, document).pipe(
      map((res: ContractApiResponse<CompanyDocument>) => res.data)
    );
  }

  deleteSupplierDocument(supplierId: string, documentId: string): Observable<any> {
    return this.apiService.delete(`${this.BASE_PATH}/suppliers/${supplierId}/documents/${documentId}`);
  }

  // Supplier Agreements
  getSupplierAgreements(supplierId: string, params?: AgreementFilterParams): Observable<Agreement[]> {
    const payload = this.buildFilterPayload(params);
    return this.apiService.create(`${this.BASE_PATH}/suppliers/${supplierId}/agreements/list`, payload).pipe(
      map((res: ContractApiResponse<Agreement[]>) => res.data)
    );
  }

  // Supplier Work Orders — DEPRECATED: Use WorkOrderService.list() instead
  // getSupplierWorkOrders(supplierId: string, params?: WorkOrderFilterParams): Observable<WorkOrder[]> {
  //   const payload = this.buildFilterPayload(params);
  //   return this.apiService.create(`${this.BASE_PATH}/suppliers/${supplierId}/work-orders/list`, payload).pipe(
  //     map((res: ContractApiResponse<WorkOrder[]>) => res.data)
  //   );
  // }

  // Supplier Candidates — DEPRECATED: Use ContractCandidateService.listCandidates() instead
  // getSupplierCandidates(supplierId: string, params?: ContractFilterParams): Observable<ContractCandidate[]> {
  //   const payload = this.buildFilterPayload(params);
  //   return this.apiService.create(`${this.BASE_PATH}/suppliers/${supplierId}/candidates/list`, payload).pipe(
  //     map((res: ContractApiResponse<ContractCandidate[]>) => res.data)
  //   );
  // }

  // ============================================================================
  // CUSTOMERS
  // ============================================================================

  getCustomers(params?: CustomerFilterParams): Observable<ContractListResponse<Customer>> {
    const payload = this.buildFilterPayload(params);
    return this.apiService.create(`${this.BASE_PATH}/customers/list`, payload).pipe(
      map((res: ContractApiResponse<ContractListResponse<Customer>>) => res.data)
    );
  }

  getCustomerById(customerId: string): Observable<Customer> {
    return this.apiService.query(`${this.BASE_PATH}/customers/${customerId}`).pipe(
      map((res: ContractApiResponse<Customer>) => {
        this.selectedCustomerSubject.next(res.data);
        return res.data;
      })
    );
  }

  createCustomer(customer: Partial<Customer>): Observable<Customer> {
    return this.apiService.create(`${this.BASE_PATH}/customers`, customer).pipe(
      map((res: ContractApiResponse<Customer>) => res.data)
    );
  }

  updateCustomer(customerId: string, customer: Partial<Customer>): Observable<Customer> {
    return this.apiService.updatePut(`${this.BASE_PATH}/customers/${customerId}`, customer).pipe(
      map((res: ContractApiResponse<Customer>) => res.data)
    );
  }

  // Customer Documents
  getCustomerDocuments(customerId: string): Observable<CompanyDocument[]> {
    return this.apiService.query(`${this.BASE_PATH}/customers/${customerId}/documents`).pipe(
      map((res: ContractApiResponse<CompanyDocument[]>) => res.data)
    );
  }

  // Customer Document Requirements
  getCustomerDocumentRequirements(customerId: string): Observable<DocumentRequirement[]> {
    return this.apiService.query(`${this.BASE_PATH}/customers/${customerId}/document-requirements`).pipe(
      map((res: ContractApiResponse<DocumentRequirement[]>) => res.data)
    );
  }

  updateCustomerDocumentRequirements(customerId: string, requirements: DocumentRequirement[]): Observable<DocumentRequirement[]> {
    return this.apiService.updatePut(`${this.BASE_PATH}/customers/${customerId}/document-requirements`, { requirements }).pipe(
      map((res: ContractApiResponse<DocumentRequirement[]>) => res.data)
    );
  }

  checkOrganizationHasRequirements(organizationId: string): Observable<boolean> {
    return this.apiService.query(`${this.BASE_PATH}/document-requirements/check/${organizationId}`).pipe(
      map((res: ContractApiResponse<{ hasRequirements: boolean }>) => res.data.hasRequirements)
    );
  }

  // Customer Agreements
  getCustomerAgreements(customerId: string, params?: AgreementFilterParams): Observable<Agreement[]> {
    const payload = this.buildFilterPayload(params);
    return this.apiService.create(`${this.BASE_PATH}/customers/${customerId}/agreements/list`, payload).pipe(
      map((res: ContractApiResponse<Agreement[]>) => res.data)
    );
  }

  // Customer Work Orders — DEPRECATED: Use WorkOrderService.list() instead
  // getCustomerWorkOrders(customerId: string, params?: WorkOrderFilterParams): Observable<WorkOrder[]> {
  //   const payload = this.buildFilterPayload(params);
  //   return this.apiService.create(`${this.BASE_PATH}/customers/${customerId}/work-orders/list`, payload).pipe(
  //     map((res: ContractApiResponse<WorkOrder[]>) => res.data)
  //   );
  // }

  // Customer Candidates — DEPRECATED: Use ContractCandidateService.listCandidates() instead
  // getCustomerCandidates(customerId: string, params?: ContractFilterParams): Observable<ContractCandidate[]> {
  //   const payload = this.buildFilterPayload(params);
  //   return this.apiService.create(`${this.BASE_PATH}/customers/${customerId}/candidates/list`, payload).pipe(
  //     map((res: ContractApiResponse<ContractCandidate[]>) => res.data)
  //   );
  // }

  // ============================================================================
  // WORK ORDERS — DEPRECATED: Use WorkOrderService instead
  // All methods below used wrong endpoints (contracts/work-orders/...)
  // Real endpoints use workOrder/ prefix — see work-order.service.ts
  // ============================================================================

  // getAllWorkOrders() — replaced by WorkOrderService.list()
  // getWorkOrderById() — replaced by WorkOrderService.getDetail()
  // createWorkOrder() — replaced by WorkOrderService.initiate()
  // updateWorkOrder() — not needed (revisions replace edits)
  // deleteWorkOrder() — not needed (void replaces delete)
  // generateWorkOrderPdf() — backend auto-generates PDF
  // initiateWorkOrderSigning() — replaced by electronic acceptance
  // getWorkOrderSigningStatus() — replaced by revision workOrderStatus
  // acceptWorkOrder() — replaced by WorkOrderService.accept()
  // rejectWorkOrder() — replaced by WorkOrderService.reject()
  // createWorkOrderRevision() — replaced by WorkOrderService.createRevision()

  // ============================================================================
  // AGREEMENTS
  // ============================================================================

  getAllAgreements(params?: AgreementFilterParams): Observable<ContractListResponse<Agreement>> {
    const payload = this.buildFilterPayload(params);
    return this.apiService.create(`${this.BASE_PATH}/agreements/list`, payload).pipe(
      map((res: ContractApiResponse<ContractListResponse<Agreement>>) => res.data)
    );
  }

  getAgreementById(agreementId: string): Observable<Agreement> {
    return this.apiService.query(`${this.BASE_PATH}/agreements/${agreementId}`).pipe(
      map((res: ContractApiResponse<Agreement>) => res.data)
    );
  }

  createAgreement(agreement: Partial<Agreement>): Observable<Agreement> {
    return this.apiService.create(`${this.BASE_PATH}/agreements`, agreement).pipe(
      map((res: ContractApiResponse<Agreement>) => res.data)
    );
  }

  updateAgreement(agreementId: string, agreement: Partial<Agreement>): Observable<Agreement> {
    return this.apiService.updatePut(`${this.BASE_PATH}/agreements/${agreementId}`, agreement).pipe(
      map((res: ContractApiResponse<Agreement>) => res.data)
    );
  }

  initiateAgreementSigning(agreementId: string): Observable<{ signingUrl: string }> {
    return this.apiService.create(`${this.BASE_PATH}/agreements/${agreementId}/initiate-signing`, {}).pipe(
      map((res: ContractApiResponse<{ signingUrl: string }>) => res.data)
    );
  }

  voidAgreement(agreementId: string, reason: string): Observable<Agreement> {
    return this.apiService.create(`${this.BASE_PATH}/agreements/${agreementId}/void`, { reason }).pipe(
      map((res: ContractApiResponse<Agreement>) => res.data)
    );
  }

  // ============================================================================
  // CONTRACT CANDIDATES — DEPRECATED: Use ContractCandidateService instead
  // ============================================================================

  // getAllContractCandidates(params?: ContractFilterParams): Observable<ContractListResponse<ContractCandidate>> {
  //   const payload = this.buildFilterPayload(params);
  //   return this.apiService.create(`${this.BASE_PATH}/candidates/list`, payload).pipe(
  //     map((res: ContractApiResponse<ContractListResponse<ContractCandidate>>) => res.data)
  //   );
  // }

  // getCandidateDocuments(candidateId: string, jobId?: string): Observable<any[]> {
  //   const payload = jobId ? { candidateId, jobId } : { candidateId };
  //   return this.apiService.create(`${this.BASE_PATH}/candidates/${candidateId}/documents`, payload).pipe(
  //     map((res: ContractApiResponse<any[]>) => res.data)
  //   );
  // }

  // verifyCandidateDocument(candidateId: string, documentId: string): Observable<any> {
  //   return this.apiService.updatePut(`${this.BASE_PATH}/candidates/${candidateId}/documents/${documentId}/verify`, {}).pipe(
  //     map((res: ContractApiResponse<any>) => res.data)
  //   );
  // }

  // rejectCandidateDocument(candidateId: string, documentId: string, reason: string): Observable<any> {
  //   return this.apiService.updatePut(`${this.BASE_PATH}/candidates/${candidateId}/documents/${documentId}/reject`, { reason }).pipe(
  //     map((res: ContractApiResponse<any>) => res.data)
  //   );
  // }

  // ============================================================================
  // EXTERNAL BUSINESS & B2B CONTACT CREATION
  // ============================================================================

  searchPlatformBusinesses(searchPayload: any): Observable<any> {
    return this.apiService.create('business/findbusiness', searchPayload);
  }

  createExternalBusiness(payload: {
    businessId: string;
    registryBusinessName: string;
    registryBusinessLogo: string;
    registryCountry?: string;
    registryState?: string;
    registryCity?: string;
    registryZipCode?: string;
    relationshipType: string;
  }): Observable<any> {
    return this.apiService.create('externalBusiness/create', payload);
  }

  createB2BContact(payload: any): Observable<any> {
    return this.apiService.create('business/createB2BContact', payload);
  }

  // ============================================================================
  // B2B SUPPLIER & CUSTOMER LISTING (Real API)
  // ============================================================================

  getB2BSuppliers(businessId: string, params?: { page?: number; limit?: number; searchContent?: string; status?: string }): Observable<PaginatedResponse<Supplier>> {
    const payload: B2BListingRequest = {
      businessId,
      type: 'Supplier',
      status: params?.status || 'CONNECTED',
      limit: params?.limit || 10,
      page: params?.page || 0,
      searchContent: params?.searchContent || ''
    };

    return this.apiService.create('business/findB2BSupplierAndCustomerList', payload).pipe(
      map((res: any) => {
        const empty: PaginatedResponse<Supplier> = { data: [], totalCount: 0, pageNumber: 0, pageSize: payload.limit, totalPages: 0 };

        if (res?.code !== '00000' || !res?.data) return empty;

        const contacts: B2BContact[] = res.data.business || [];
        const suppliers: Supplier[] = contacts.map(c => this.mapB2BToSupplier(c));
        const totalCount = res.data.total || suppliers.length;
        return {
          data: suppliers,
          totalCount,
          pageNumber: (payload.page || 1) - 1,
          pageSize: payload.limit,
          totalPages: Math.ceil(totalCount / payload.limit)
        };
      })
    );
  }

  getB2BCustomers(businessId: string, params?: { page?: number; limit?: number; searchContent?: string; status?: string }): Observable<PaginatedResponse<Customer>> {
    const payload: B2BListingRequest = {
      businessId,
      type: 'Client',
      status: params?.status || 'CONNECTED',
      limit: params?.limit || 10,
      page: params?.page || 0,
      searchContent: params?.searchContent || ''
    };

    return this.apiService.create('business/findB2BSupplierAndCustomerList', payload).pipe(
      map((res: any) => {
        const empty: PaginatedResponse<Customer> = { data: [], totalCount: 0, pageNumber: 0, pageSize: payload.limit, totalPages: 0 };

        if (res?.code !== '00000' || !res?.data) return empty;

        const contacts: B2BContact[] = res.data.business || [];
        const customers: Customer[] = contacts.map(c => this.mapB2BToCustomer(c));
        const totalCount = res.data.total || customers.length;
        return {
          data: customers,
          totalCount,
          pageNumber: (payload.page || 1) - 1,
          pageSize: payload.limit,
          totalPages: Math.ceil(totalCount / payload.limit)
        };
      })
    );
  }

  private mapB2BToSupplier(contact: B2BContact): Supplier {
    const loc = contact.companyLocationDetails?.[0];
    const partnerId = contact.peerBusinessId || contact.businessId;
    console.log('[mapB2BToSupplier] businessId:', contact.businessId, 'peerBusinessId:', contact.peerBusinessId, '→ supplierId:', partnerId);
    return {
      supplierId: partnerId,
      gsBusinessId: contact.platformBusinesId,
      companyName: contact.businessName,
      location: [loc?.city, loc?.state].filter(Boolean).join(', '),
      workOrderCount: 0,
      activeWorkOrderCount: contact.activeWorkOrderCount || 0,
      createdOn: contact.b2bContact?.createdOn || contact.createdOn || '',
      lastUpdated: contact.b2bContact?.modifiedOn || contact.lastLoggedIn || '',
      status: this.mapB2BStatus(contact.connectionStatus) as SupplierStatus,
      externalBusiness: contact.externalBusiness || false,
      companyProfile: {
        businessId: partnerId,
        legalName: contact.businessName,
        address: { city: loc?.city || '', state: loc?.state || '' },
        phone: loc?.businessPhone || '',
        industry: contact.industryClassification,
        employeeCount: contact.companySize
      }
    };
  }

  private mapB2BToCustomer(contact: B2BContact): Customer {
    const loc = contact.companyLocationDetails?.[0];
    const partnerId = contact.peerBusinessId || contact.businessId;
    console.log('[mapB2BToCustomer] businessId:', contact.businessId, 'peerBusinessId:', contact.peerBusinessId, '→ customerId:', partnerId);
    return {
      customerId: partnerId,
      gsBusinessId: contact.platformBusinesId,
      companyName: contact.businessName,
      location: [loc?.city, loc?.state].filter(Boolean).join(', '),
      workOrderCount: 0,
      activeWorkOrderCount: contact.activeWorkOrderCount || 0,
      createdOn: contact.b2bContact?.createdOn || contact.createdOn || '',
      lastUpdated: contact.b2bContact?.modifiedOn || contact.lastLoggedIn || '',
      status: this.mapB2BStatus(contact.connectionStatus) as CustomerStatus,
      externalBusiness: contact.externalBusiness || false,
      companyProfile: {
        businessId: partnerId,
        legalName: contact.businessName,
        address: { city: loc?.city || '', state: loc?.state || '' },
        phone: loc?.businessPhone || '',
        industry: contact.industryClassification,
        employeeCount: contact.companySize
      }
    };
  }

  private mapB2BStatus(apiStatus: string): string {
    const statusMap: Record<string, string> = {
      'CONNECTED': 'ACTIVE',
      'PENDING': 'IN_PROGRESS',
      'REJECTED': 'INACTIVE',
      'INACTIVE': 'INACTIVE'
    };
    return statusMap[apiStatus] || 'DRAFT';
  }

  // ============================================================================
  // B2B CONTACT DETAIL (Single B2B relationship lookup)
  // ============================================================================

  /**
   * Get B2B contact details between two businesses
   * GET /getB2BContact/{businessId}/{peerBusinessId}
   */
  getB2BContact(businessId: string, peerBusinessId: string): Observable<any> {
    return this.apiService.query(`business/getB2BContact/${businessId}/${peerBusinessId}`);
  }

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  setSelectedSupplier(supplier: Supplier | null): void {
    this.selectedSupplierSubject.next(supplier);
  }

  setSelectedCustomer(customer: Customer | null): void {
    this.selectedCustomerSubject.next(customer);
  }

  setSelectedWorkOrder(workOrder: WorkOrder | null): void {
    this.selectedWorkOrderSubject.next(workOrder);
  }

  clearSelections(): void {
    this.selectedSupplierSubject.next(null);
    this.selectedCustomerSubject.next(null);
    this.selectedWorkOrderSubject.next(null);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private buildFilterPayload(params?: ContractFilterParams): any {
    if (!params) {
      return {
        pageNumber: 0,
        pageSize: 10
      };
    }

    const payload: any = {
      pageNumber: params.pageNumber ?? 0,
      pageSize: params.pageSize ?? 10
    };

    if (params.searchText) {
      payload.searchText = params.searchText;
    }

    if (params.status) {
      payload.status = params.status;
    }

    if (params.dateRange) {
      payload.fromDate = params.dateRange.from;
      payload.toDate = params.dateRange.to;
    }

    if (params.sortBy) {
      payload.sortBy = params.sortBy;
      payload.sortOrder = params.sortOrder || 'asc';
    }

    return payload;
  }

  // Status label helpers
  getStatusLabel(status: string): string {
    const statusLabels: Record<string, string> = {
      'DRAFT': 'Draft',
      'IN_PROGRESS': 'In Progress',
      'ACTIVE': 'Active',
      'INACTIVE': 'Inactive',
      'PENDING_UPLOAD': 'Pending Upload',
      'UPLOADED': 'Uploaded',
      'PENDING_VERIFICATION': 'Pending Verification',
      'VERIFIED': 'Verified',
      'REJECTED': 'Rejected',
      'EXPIRED': 'Expired',
      'PENDING_SIGNATURE': 'Pending Signature',
      'COMPLETED': 'Completed',
      'DECLINED': 'Declined',
      'VOIDED': 'Voided',
      'INITIATED': 'Initiated',
      'VOID': 'Closed',
      'ARCHIVED': 'Archived'
    };

    return statusLabels[status] || status;
  }

  getStatusColor(status: string): string {
    const statusColors: Record<string, string> = {
      'DRAFT': 'secondary',
      'IN_PROGRESS': 'warning',
      'ACTIVE': 'success',
      'INACTIVE': 'secondary',
      'PENDING_UPLOAD': 'info',
      'UPLOADED': 'info',
      'PENDING_VERIFICATION': 'warning',
      'VERIFIED': 'success',
      'REJECTED': 'danger',
      'EXPIRED': 'warning',
      'PENDING_SIGNATURE': 'warning',
      'COMPLETED': 'success',
      'DECLINED': 'danger',
      'VOIDED': 'secondary',
      'INITIATED': 'info',
      'VOID': 'secondary',
      'ARCHIVED': 'secondary'
    };

    return statusColors[status] || 'secondary';
  }
}
